import { useEffect, useMemo, useState } from 'react';

import { Button, Center, Flex, Spinner, Text } from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import { CheckOutline, useToast } from '@aslanpay/portal-common-lib';

import { ToggleRow } from 'app/components/Settings/ToggleRow';

import ProfileSection from 'app/components/UI/ProfileSection';

import useFetchCompanyConfiguration from 'app/hooks/useFetchCompanyConfiguration';
import useUpdateCompanyConfiguration from 'app/hooks/useUpdateCompanyConfiguration';

import { NotificationsFormState, NotificationsTabProps } from './NotificationsTab.types';

function NotificationsTab({ companyId = '' }: NotificationsTabProps) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: companyConfiguration, isInitialLoading } = useFetchCompanyConfiguration(companyId);

  const { mutateAsync: updateCompanyConfigurationAsync } = useUpdateCompanyConfiguration({
    onSuccess: () => {},
    onError: () => {},
  });

  const [isSaving, setIsSaving] = useState(false);

  const initialForm = useMemo<NotificationsFormState>(() => {
    const config = companyConfiguration?.notificationsConfig;
    return {
      welcomeEmailEnabled: config?.welcomeEmailEnabled ?? true,
      invitationEmailEnabled: config?.invitationEmailEnabled ?? true,
      earningsAvailableEmailEnabled: config?.earningsAvailableEmailEnabled ?? false,
    };
  }, [companyConfiguration]);

  const [form, setForm] = useState<NotificationsFormState>(initialForm);

  useEffect(() => {
    setForm(initialForm);
  }, [initialForm]);

  const updateField = <Key extends keyof NotificationsFormState>(
    key: Key,
    value: NotificationsFormState[Key]
  ) => {
    setForm((previous) => ({ ...previous, [key]: value }));
  };

  const hasChanged = useMemo(
    () =>
      (Object.keys(initialForm) as (keyof NotificationsFormState)[]).some(
        (key) => form[key] !== initialForm[key]
      ),
    [form, initialForm]
  );

  const isSaveDisabled = !hasChanged || isSaving;

  const handleSave = async () => {
    if (!companyConfiguration) return;

    setIsSaving(true);
    try {
      await updateCompanyConfigurationAsync({
        id: companyConfiguration.companyId,
        notificationsConfig: {
          welcomeEmailEnabled: form.welcomeEmailEnabled,
          invitationEmailEnabled: form.invitationEmailEnabled,
          earningsAvailableEmailEnabled: form.earningsAvailableEmailEnabled,
        },
      });

      toast({
        position: 'top-right',
        status: 'success',
        title: 'Configuration saved',
        description: 'The notifications configuration was successfully saved.',
      });

      queryClient.invalidateQueries({
        queryKey: ['fetchCompanyConfiguration', companyConfiguration.companyId],
      });
    } catch (error) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'It was not possible to save the notifications configuration.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isInitialLoading) {
    return (
      <Center width="full" py="40px">
        <Spinner color="brand.purple" size="xl" />
      </Center>
    );
  }

  return (
    <Flex flexDir="column" gap="20px" alignItems="stretch" w="full">
      <ProfileSection title="Onboarding emails" isEditMode maxW="100%">
        <Text textStyle="extraSmallRegular" color="brand.darkGray">
          Controls the emails an individual receives when they are invited to Aslan. Switching one
          off applies to everyone at this company invited from that point on.
        </Text>

        <ToggleRow
          label="Send welcome emails to employees"
          description="Email employees when they are invited to Aslan."
          isChecked={form.welcomeEmailEnabled}
          onChange={(checked) => updateField('welcomeEmailEnabled', checked)}
          testId="welcome-email-switch"
        />
        <ToggleRow
          label="Send invitation emails to admins"
          description="Email company admins and other non-employee users when they are invited. Switching this off means they will not receive a link to finish registration."
          isChecked={form.invitationEmailEnabled}
          onChange={(checked) => updateField('invitationEmailEnabled', checked)}
          testId="invitation-email-switch"
        />
      </ProfileSection>

      <ProfileSection title="Earnings emails" isEditMode maxW="100%">
        <ToggleRow
          label="Send earnings available emails to employees"
          description="Email employees when their approved earnings arrive from a timesheet import and their balance is available. Off unless switched on."
          isChecked={form.earningsAvailableEmailEnabled}
          onChange={(checked) => updateField('earningsAvailableEmailEnabled', checked)}
          testId="earnings-available-email-switch"
        />
      </ProfileSection>

      <Button
        leftIcon={<CheckOutline boxSize="20px" />}
        size="sm"
        alignSelf="flex-start"
        onClick={handleSave}
        isLoading={isSaving}
        isDisabled={isSaveDisabled}
        data-testid="save-notifications-config-button"
      >
        Save changes
      </Button>
    </Flex>
  );
}

export { NotificationsTab };
